import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';
import { Key, X } from 'lucide-react';

interface User {
  id: number;
  email: string;
  name: string;
  role: 'USER' | 'ADMIN' | 'MASTER' | 'SUPER_ADMIN';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean | 'mfa_required'>;
  register: (email: string, name: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  bootError: 'network' | null;
  retryBoot: () => void;
}

const BOOT_AUTH_TIMEOUT_MS = 8000;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState<'network' | null>(null);
  const [passwordExpiry, setPasswordExpiry] = useState<{ days: number; hours: number | null } | null>(null);

  const handleLogout = useCallback(() => {
    setUser(null);
    tokenStorage.clearTokens();
    delete api.defaults.headers.common['Authorization'];
    toast.error('Session expired. Please login again.');

    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
  }, []);

  const verifyToken = useCallback(async (isBoot = false) => {
    try {
      const response = await api.get('/api/auth/me', isBoot ? { timeout: BOOT_AUTH_TIMEOUT_MS } : undefined);
      setUser(response.data.user);
      if (isBoot) setBootError(null);
    } catch (error: any) {
      const status = error.response?.status;
      const isTimeoutOrNetwork = !error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error');

      if (status === 401 || status === 403) {
        tokenStorage.clearTokens();
        delete api.defaults.headers.common['Authorization'];
        setUser(null);

        if (status === 403 && error.response?.data?.code === 'IP_NOT_WHITELISTED') {
          toast.error('Access denied: Your IP address is not authorized');
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
        }
      } else if (isBoot && isTimeoutOrNetwork) {
        setBootError('network');
      }
    } finally {
      if (isBoot) setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyToken(true);

    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [handleLogout, verifyToken]);

  useEffect(() => {
    const sessionMonitor = setInterval(() => {
      if (user) {
        verifyToken(false);
      }
    }, 60000);

    return () => {
      clearInterval(sessionMonitor);
    };
  }, [user, verifyToken]);

  const retryBoot = useCallback(() => {
    setLoading(true);
    setBootError(null);
    verifyToken(true);
  }, [verifyToken]);

  const login = async (email: string, password: string): Promise<boolean | 'mfa_required'> => {
    try {
      const response = await api.post('/api/auth/login', { email, password });

      if (response.data.requiresMfa || response.data.requiresMFA) {
        return 'mfa_required';
      }

      const { user, accessToken, refreshToken, expiresIn } = response.data;

      if (!user) {
        throw new Error('Invalid login response');
      }

      if (accessToken && refreshToken) {
        tokenStorage.setTokens(accessToken, refreshToken, expiresIn);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      }

      setUser(user);

      toast.success('Login successful');

      const daysLeft = response.data.passwordExpiresInDays;
      const hoursLeft = response.data.passwordExpiresInHours;
      if (daysLeft != null && daysLeft <= 7) {
        setPasswordExpiry({ days: daysLeft, hours: hoursLeft ?? null });
      }

      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      return false;
    }
  };

  const register = async (email: string, name: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post('/api/auth/register', { email, name, password });
      const { user, accessToken, refreshToken, expiresIn } = response.data;

      if (accessToken && refreshToken) {
        tokenStorage.setTokens(accessToken, refreshToken, expiresIn);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      }

      setUser(user);

      toast.success('Registration successful');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout', { logoutAll: false });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      tokenStorage.clearTokens();
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    bootError,
    retryBoot
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {passwordExpiry != null && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/40"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full max-w-sm mx-0 sm:mx-4 p-6 relative"
            style={{ animation: 'slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}>
            <style>{`
              @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
              @keyframes slideUp { from { transform: translateY(40px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
            `}</style>
            <button
              onClick={() => setPasswordExpiry(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-full ${passwordExpiry.days <= 2 ? 'bg-red-100' : 'bg-amber-100'}`}>
                <Key size={20} className={passwordExpiry.days <= 2 ? 'text-red-600' : 'text-amber-600'} />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Password Expiring Soon</h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              {passwordExpiry.days === 0 && passwordExpiry.hours != null
                ? `Your password expires in ${passwordExpiry.hours} hour${passwordExpiry.hours !== 1 ? 's' : ''}. Please change it now to avoid losing access.`
                : passwordExpiry.days === 1
                  ? 'Your password expires tomorrow. Please change it now to avoid losing access.'
                  : `Your password expires in ${passwordExpiry.days} days. Update it now to avoid losing access.`}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setPasswordExpiry(null); window.location.href = '/app/settings?tab=profile'; }}
                className="flex-1 text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg transition-colors"
              >
                Change Password
              </button>
              <button
                onClick={() => setPasswordExpiry(null)}
                className="flex-1 text-center border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded-lg transition-colors"
              >
                Remind Me Later
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
